/**
 * Grid class for managing transform handles around selected entities.
 *
 * Responsibilities:
 * - Create and display transform handles based on selection
 * - Wire overlay drag events directly to handle methods
 * - Manage transform lifecycle (start/ongoing/end)
 * - Emit transform results for command creation
 */

import type { DRDrawingViewer } from "../DRDrawingViewer";
import type { DrawingModuleType } from "../DrawingSingleton";
import type { DrawingModule } from "../types/DrawingWeb";
import type { CommandHistory } from "../commands/CommandHistory";
import type {
  OverlayManager,
  InteractiveLayer,
  HandleOverlay,
  HitTestResult,
} from "../overlay";
import { OverlayType, CoordinateSystem } from "../overlay";
import type { IHandle } from "./handles/IHandle";
import type { KeyPoints, Point3D, Point2D, TransformContext } from "./types";
import { TRANSFORM_THEME, HANDLE_SIZES } from "./types";
import { buildHandleIconPath, buildHandleShapePoints } from "./handleShapes";
import { selectVisibleHandles } from "./handleVisibility";
import { computeKeyPoints } from "./gridKeyPoints";
import { buildEntityHandles, buildGroupHandles } from "./gridHandles";
import { Logger } from "@/logger";

/** Role prefix for transform handles */
export const TRANSFORM_HANDLE_ROLE = "transform";

/** Dashed line of the bounding box, reused for the handles tied to it. */
const PERIMETER_STYLE = {
  strokeColor: `rgb(${TRANSFORM_THEME.gridPerimeter.join(", ")})`,
  strokeWidth: 1,
  lineDash: [4, 4],
};

/**
 * What a grid stands for in the selection:
 * - "entity": the only selected entity, its handles and bounding box;
 * - "member": one entity of a multiple selection, its handles without its box
 *   (the shared box of the "group" grid is the only one drawn);
 * - "group": a multiple selection, its shared box and the handle moving it all.
 */
export type GridRole = "entity" | "member" | "group";

/**
 * Notified of the transforms of a grid, so that the other grids of the same
 * selection follow: hidden during the drag, rebuilt after it (a moved entity
 * changes the shared box, a moved group changes every entity's handles).
 */
export interface GridTransformListener {
  onTransformStart(grid: Grid): void;
  /** Committed: the entities may have moved. */
  onTransformEnd(grid: Grid): void;
  /** Cancelled or failed: nothing moved. */
  onTransformAbort(grid: Grid): void;
}

/**
 * Callback for snapping points during vertex handle movement.
 */
export type SnapPointCallback = (
  worldPoint: Point3D,
  screenPoint: Point2D,
) => Point3D;

/**
 * Grid represents the visual selection grid and manages handles.
 */
export class Grid {
  private viewer: DRDrawingViewer;
  private history: CommandHistory;
  private module: DrawingModuleType;
  private overlayManager: OverlayManager;
  private interactiveLayer: InteractiveLayer;

  /** Selected entity IDs */
  private entityIds: DrawingModule.OdDbObjectId[] = [];

  /** Calculated key points */
  private keyPoints: KeyPoints | null = null;

  /** Active handles */
  private handles: IHandle[] = [];

  /** Overlay IDs for perimeter and handles */
  private perimeterOverlayId: string | null = null;
  private handleOverlayIds: string[] = [];
  /** Handle icons and tethers, drawn around them (not interactive) */
  private decorationOverlayIds: string[] = [];

  /** Whether to show perimeter (false for non-resizable entities like polyline, text) */
  private showPerimeter = true;

  /** Map overlay ID to IHandle for routing */
  private overlayToHandle = new Map<string, IHandle>();

  // ============== Transform State ==============

  /** Currently active handle during transform */
  private activeHandle: IHandle | null = null;

  /** Transform start context */
  private startContext: TransformContext | null = null;

  /** Whether a transform is in progress */
  private _isTransforming = false;

  private role: GridRole = "entity";
  private transformListener: GridTransformListener | null;

  constructor(
    viewer: DRDrawingViewer,
    history: CommandHistory,
    overlayManager: OverlayManager,
    layer: InteractiveLayer,
    transformListener: GridTransformListener | null = null,
  ) {
    this.viewer = viewer;
    this.history = history;
    this.module = viewer.module;
    this.overlayManager = overlayManager;
    this.interactiveLayer = layer;
    this.transformListener = transformListener;
  }

  /**
   * Set the interactive layer for handle registration.
   */
  setInteractiveLayer(layer: InteractiveLayer): void {
    this.interactiveLayer = layer;
  }

  /**
   * Check if a transform is in progress.
   */
  get isTransforming(): boolean {
    return this._isTransforming;
  }

  /**
   * Set entities and build the grid.
   */
  setEntities(
    entityIds: DrawingModule.OdDbObjectId[],
    role: GridRole = "entity",
  ): void {
    // Don't update grid during active transform
    if (this._isTransforming) return;

    this.clear();

    if (entityIds.length === 0) return;

    this.entityIds = entityIds;
    this.role = role;

    // Calculate key points from entity bounds
    this.keyPoints = computeKeyPoints(this.module, entityIds);

    if (!this.keyPoints) return;

    const ctx = {
      viewer: this.viewer,
      history: this.history,
      module: this.module,
      entityIds,
      keyPoints: this.keyPoints,
    };
    const { handles, showPerimeter } =
      role === "group" ? buildGroupHandles(ctx) : buildEntityHandles(ctx);
    this.handles = handles;
    this.showPerimeter = showPerimeter && role !== "member";

    // Draw the grid
    this.draw();
  }

  /**
   * Get entities.
   */
  getEntities(): DrawingModule.OdDbObjectId[] {
    return this.entityIds;
  }

  /**
   * Get key points.
   */
  getKeyPoints(): KeyPoints | null {
    return this.keyPoints;
  }

  /**
   * Get handles.
   */
  getHandles(): IHandle[] {
    return this.handles;
  }

  /**
   * Find a handle at the given world point.
   */
  findHandleAt(worldPoint: Point3D): IHandle | null {
    // Convert screen tolerance to world units
    const tolerance = this.viewer.screenDistanceToWorld(
      HANDLE_SIZES.hitTolerance,
    );

    for (const handle of this.handles) {
      if (handle.hitTest(worldPoint, tolerance)) {
        return handle;
      }
    }

    return null;
  }

  /**
   * Clear the grid.
   */
  clear(): void {
    // Cancel any active transform
    if (this._isTransforming) {
      this.cancelTransform();
    }

    // Dispose handles
    for (const handle of this.handles) {
      handle.dispose();
    }
    this.handles = [];

    // Remove overlays
    this.clearOverlays();

    this.entityIds = [];
    this.keyPoints = null;
    this.showPerimeter = true; // Reset to default
  }

  /**
   * Cancel current transform.
   */
  cancelTransform(): void {
    if (!this._isTransforming || !this.activeHandle) return;

    const handle = this.activeHandle;

    // Reset state first (in case cancel() throws)
    this.activeHandle = null;
    this._isTransforming = false;
    this.startContext = null;

    try {
      handle.cancel();
    } catch (error) {
      Logger.captureException(error, {
        source: `${handle.constructor.name}.cancel (Grid.cancelTransform)`,
      });
      this.transformListener?.onTransformAbort(this);
      return;
    }

    // Show grid again
    this.show();
    this.transformListener?.onTransformAbort(this);
    this.viewer.update();
  }

  /**
   * Redraw the grid (e.g., after zoom).
   */
  redraw(): void {
    this.clearOverlays();
    if (this.keyPoints) {
      this.draw();
    }
  }

  /**
   * Hide the grid temporarily.
   */
  hide(): void {
    this.clearOverlays();
  }

  /**
   * Show the grid.
   */
  show(): void {
    if (this.keyPoints) {
      this.draw();
    }
  }

  // ============== Private Methods ==============

  /**
   * Draw the grid perimeter and handles.
   */
  private draw(): void {
    if (!this.keyPoints) return;

    this.beforeRedraw();

    // Draw perimeter rectangle (only for resizable entities)
    if (this.showPerimeter) {
      this.drawPerimeter();
    }

    // Draw handles
    this.drawHandles();
  }

  /**
   * Called each handle beforeRedraw method.
   * If Handles need to update context like constant in Pixel before redraw is done
   */

  beforeRedraw() {
    for (const handle of this.handles) {
      if (handle.beforeRedraw) handle.beforeRedraw();
    }
  }

  /**
   * Draw the selection perimeter.
   */
  private drawPerimeter(): void {
    if (!this.keyPoints) return;

    const { topLeft, topRight, bottomRight, bottomLeft } = this.keyPoints;

    // Convert to screen coordinates
    const tl = this.viewer.worldToScreen(topLeft);
    const tr = this.viewer.worldToScreen(topRight);
    const br = this.viewer.worldToScreen(bottomRight);
    const bl = this.viewer.worldToScreen(bottomLeft);

    this.perimeterOverlayId = this.overlayManager.createPolyline(
      [tl, tr, br, bl, tl],
      PERIMETER_STYLE,
    );
  }

  /**
   * Draw all handles.
   * Registers them with InteractiveLayer for hit testing.
   * Wires drag events directly to handle methods.
   */
  private drawHandles(): void {
    // Calculate which handles should be visible based on overlap
    const visibleHandles = selectVisibleHandles(this.handles, (point) =>
      this.viewer.worldToScreen(point),
    );

    for (const handle of this.handles) {
      // Skip handles that are hidden due to overlap
      if (!visibleHandles.has(handle)) {
        continue;
      }
      const screenPos = this.viewer.worldToScreen(handle.worldPosition);
      const [fr, fg, fb] = handle.drawOptions.fillColor;
      const [sr, sg, sb] = handle.drawOptions.strokeColor;
      const fillColor = `rgb(${fr}, ${fg}, ${fb})`;
      const strokeColor = `rgb(${sr}, ${sg}, ${sb})`;
      // Register as interactive handle for hit testing
      const handleOverlay: Omit<HandleOverlay, "id"> = {
        type: OverlayType.Handle,
        interactive: true,
        coordinateSystem: CoordinateSystem.World,
        worldPosition: { ...handle.worldPosition },
        screenPosition: screenPos,
        shape: "circle",
        size: handle.drawOptions.radius * 2,
        snapOptions: handle.snapOptions,
        role: TRANSFORM_HANDLE_ROLE,
        dragData: handle,
        hitRadius: handle.drawOptions.radius + 5,
        cursor: "move",
        style: {
          fillColor,
          strokeColor,
          strokeWidth: 2,
        },
        // Drag handlers - wired directly to handle methods
        onDragStart: (hit: HitTestResult) => this.handleDragStart(handle, hit),
        onDragMove: (screen, world) => this.handleDragMove(screen, world),
        onDragEnd: (screen, world) => this.handleDragEnd(screen, world),
      };

      const id = this.interactiveLayer.addInteractive(handleOverlay);
      this.handleOverlayIds.push(id);
      this.overlayToHandle.set(id, handle);

      // Update overlay so it actually renders — OverlayManager doesn't draw the
      // Handle type directly. Circles go through Point; the dynamic block glyphs
      // (arrow, triangle, square) are closed filled polylines in screen space.
      const style = { fillColor, strokeColor, strokeWidth: 2 };
      const shapePoints = buildHandleShapePoints(
        handle.drawOptions.shape,
        screenPos,
        handle.drawOptions.radius,
      );

      this.overlayManager.update(
        id,
        shapePoints
          ? {
              type: OverlayType.Polyline,
              points: shapePoints,
              closed: true,
              style,
            }
          : {
              type: OverlayType.Point,
              x: screenPos.x,
              y: screenPos.y,
              radius: handle.drawOptions.radius,
              style,
            },
      );

      this.drawHandleDecorations(handle, screenPos);
    }
  }

  /** Inner icon and tether line of a handle, when it has them. */
  private drawHandleDecorations(handle: IHandle, screenPos: Point2D): void {
    const { innerIcon, tether, radius } = handle.drawOptions;

    if (tether) {
      // From the tether point to the edge of the disc (contour included).
      const from = this.viewer.worldToScreen(tether);
      const length = Math.hypot(screenPos.x - from.x, screenPos.y - from.y);
      const gap = radius + 1;
      if (length > gap) {
        const ratio = (length - gap) / length;
        const to = {
          x: from.x + (screenPos.x - from.x) * ratio,
          y: from.y + (screenPos.y - from.y) * ratio,
        };
        this.decorationOverlayIds.push(
          this.overlayManager.createPolyline([from, to], PERIMETER_STYLE),
        );
      }
    }

    if (innerIcon) {
      this.decorationOverlayIds.push(
        this.overlayManager.createPolyline(
          buildHandleIconPath(innerIcon, screenPos, radius),
          { strokeColor: "#ffffff", strokeWidth: radius * 0.08 },
        ),
      );
    }
  }

  // ============== Transform Lifecycle ==============

  /**
   * Handle drag start - start transform on the handle.
   */
  private handleDragStart(handle: IHandle, hit: HitTestResult): void {
    if (this._isTransforming) return;

    this.activeHandle = handle;
    this._isTransforming = true;

    this.startContext = {
      screenPoint: hit.screenPoint,
      worldPoint: hit.worldPoint,
      startScreenPoint: hit.screenPoint,
      startWorldPoint: hit.worldPoint,
      shiftKey: false,
      ctrlKey: false,
    };

    // Hide grid during transform
    this.hide();
    this.transformListener?.onTransformStart(this);

    // Start the handle transform
    handle.start(this.startContext);
  }

  /**
   * Handle drag move - update transform preview.
   */
  private handleDragMove(screenPoint: Point2D, worldPoint: Point3D): void {
    if (!this._isTransforming || !this.activeHandle || !this.startContext)
      return;

    const context: TransformContext = {
      screenPoint,
      worldPoint,
      startScreenPoint: this.startContext.startScreenPoint,
      startWorldPoint: this.startContext.startWorldPoint,
      shiftKey: false,
      ctrlKey: false,
    };

    this.activeHandle.ongoing(context);
  }

  /**
   * Handle drag end - finalize transform.
   * Handles execute commands internally; we just need to refresh the grid.
   */
  private handleDragEnd(screenPoint: Point2D, worldPoint: Point3D): void {
    if (!this._isTransforming || !this.activeHandle || !this.startContext)
      return;

    const context: TransformContext = {
      screenPoint,
      worldPoint,
      startScreenPoint: this.startContext.startScreenPoint,
      startWorldPoint: this.startContext.startWorldPoint,
      shiftKey: false,
      ctrlKey: false,
    };

    // Store active handle reference before clearing state
    const handle = this.activeHandle;

    // Reset state first (in case end() throws)
    this.activeHandle = null;
    this._isTransforming = false;
    this.startContext = null;

    // End the transform - handle executes command internally.
    // Caught so a WASM crash here doesn't propagate uncaught and leave
    // subsequent operations (e.g. selection) working against a corrupted module.
    try {
      handle.end(context);
    } catch (error) {
      Logger.captureException(error, {
        source: `${handle.constructor.name}.end (Grid.handleDragEnd)`,
      });
      this.transformListener?.onTransformAbort(this);
      return;
    }

    // Recalculate the grid(s) at the new position (entities may have moved)
    if (this.transformListener) {
      this.transformListener.onTransformEnd(this);
    } else {
      const entities = this.entityIds;
      this.entityIds = []; // Clear to force recalculation
      this.setEntities(entities, this.role);
    }

    // Update view
    this.viewer.update();
  }

  /**
   * Clear overlay graphics.
   */
  private clearOverlays(): void {
    if (this.perimeterOverlayId) {
      this.overlayManager.remove(this.perimeterOverlayId);
      this.perimeterOverlayId = null;
    }

    for (const id of this.handleOverlayIds) {
      if (this.interactiveLayer) {
        this.interactiveLayer.removeInteractive(id);
      } else {
        this.overlayManager.remove(id);
      }
    }
    this.handleOverlayIds = [];

    for (const id of this.decorationOverlayIds) this.overlayManager.remove(id);
    this.decorationOverlayIds = [];
    this.overlayToHandle.clear();
  }
}
